// MultiTimingTab — Phase 4-B (2026-05-11)
//
// 学習機構 多 timing hook の閲覧 dashboard。
// - hook 発火 events 一覧 (source / event_type 別)
// - hook 結論 conflict 一覧 + 解決
// - 統計 dashboard (source / partner 別 hook 数)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CardBase,
  Note,
  Button,
  MessageBlock,
  SubSectionTitle,
  SelectBox,
  InlineSpinner,
  TextField,
  TabBar,
} from '@freee_jp/vibes';
import {
  MdSchool,
  MdRefresh,
  MdWarning,
  MdCompareArrows,
  MdCheckCircle,
  MdHistory,
  MdAccountTree,
  MdSearch,
  MdInsights,
} from 'react-icons/md';
import {
  fetchLearningEvents,
  fetchLearningConflicts,
  resolveLearningConflict,
  LearningEvent,
  LearningEventBreakdown,
  LearningConflict,
} from '../../api/client';

const fmtDate = (s: string | null | undefined): string =>
  s ? String(s).slice(0, 19).replace('T', ' ') : '—';

const SOURCES = [
  { name: 'すべて', value: '' },
  { name: '銀行 register (= recordBankPatternOutcome)', value: '/api/bank-wallet-txns/:wt/approve' },
  { name: 'reconcile_success', value: 'markD1ReconcileSuccess' },
  { name: 'freee inline sync', value: 'upsertWalletTxn' },
  { name: 'chat ingest', value: '/api/admin/learning-chat-ingest' },
  { name: 'multi-reader-extract (PDF vision)', value: '/api/admin/invoice-multi-reader-extract' },
  { name: 'invoice-layout-outcome', value: '/api/admin/invoice-layout-outcome' },
  { name: 'validate-3way-amount', value: '/api/admin/validate-3way-amount' },
  { name: 'accounting-audit', value: '/api/admin/invoice-accounting-audit' },
  { name: 'partner-details/sync', value: '/api/partner-details/sync' },
  { name: 'retroactive backfill', value: 'retroactive_backfill_v33' },
  { name: 'monthly rebuild', value: 'cron_monthly_rebuild' },
  { name: 'admin recover', value: '/api/admin/learning-recover' },
];

const EVENT_TYPES = [
  { name: 'すべて', value: '' },
  { name: 'reconcile_success', value: 'reconcile_success' },
  { name: 'approve', value: 'approve' },
  { name: 'reject', value: 'reject' },
  { name: 'freee_auto_sync', value: 'freee_auto_sync' },
  { name: 'mark_registered', value: 'mark_registered' },
  { name: 'manual_chat_learning', value: 'manual_chat_learning' },
  { name: 'pattern-commit', value: 'pattern-commit' },
  // Phase 4-B hook events
  { name: 'vision_paradigm_learn (Phase 4-B)', value: 'vision_paradigm_learn' },
  { name: 'past_deal_autofill (Phase 4-B)', value: 'past_deal_autofill' },
  { name: '3way_to_bank_link (Phase 4-B)', value: '3way_to_bank_link' },
  { name: 'category_canonical (Phase 4-B)', value: 'category_canonical' },
  { name: 'auto_debit_detected (Phase 4-B)', value: 'auto_debit_detected' },
  { name: 'multi_store_split_learn (Phase 4-B)', value: 'multi_store_split_learn' },
];

const SUB_TABS = ['events', 'conflicts', 'breakdown'] as const;
type SubTabKey = (typeof SUB_TABS)[number];

export const MultiTimingTab: React.FC = () => {
  const [subTab, setSubTab] = useState<SubTabKey>('events');

  return (
    <>
      <Note>
        Phase 4-B (2026-05-11): 学習機構 多 timing hook events / conflicts / integrity 閲覧。
        承認後だけでなく PDF vision 抽出時 / 過去 deal sync 時 / 3-way validation 時 / category 確定時 /
        bank_auto_debit 検出時 / multi-store split 確定時 にも学習発火。
      </Note>

      <CardBase paddingSize="small" mb={0.5}>
        <TabBar
          tabs={['イベント trail', '結論不一致', '統計 breakdown']}
          currentTabIndex={SUB_TABS.indexOf(subTab)}
          onClickTab={(i: number) => setSubTab(SUB_TABS[i])}
        />
      </CardBase>

      {subTab === 'events' && <EventsView />}
      {subTab === 'conflicts' && <ConflictsView />}
      {subTab === 'breakdown' && <BreakdownView />}
    </>
  );
};

// ---------------------------------------------------------------------------
// EventsView
// ---------------------------------------------------------------------------
const EventsView: React.FC = () => {
  const [events, setEvents] = useState<LearningEvent[]>([]);
  const [breakdown, setBreakdown] = useState<LearningEventBreakdown[]>([]);
  const [source, setSource] = useState('');
  const [eventType, setEventType] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchLearningEvents({
        source: source || undefined,
        event_type: eventType || undefined,
        partner_id: partnerId ? Number(partnerId) : undefined,
        limit: 100,
      });
      setEvents(r.events);
      setBreakdown(r.breakdown);
    } catch (e: any) {
      setError(e?.message || String(e));
    }
    setLoading(false);
  }, [source, eventType, partnerId]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

      {/* Filters */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>
            <MdSearch style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Filter
          </SubSectionTitle>
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
            <FilterCell label="source_path">
              <SelectBox options={SOURCES.map(s => ({ name: s.name, value: s.value }))}
                         value={source} onChange={(e) => setSource(e.target.value)} />
            </FilterCell>
            <FilterCell label="event_type">
              <SelectBox options={EVENT_TYPES.map(s => ({ name: s.name, value: s.value }))}
                         value={eventType} onChange={(e) => setEventType(e.target.value)} />
            </FilterCell>
            <FilterCell label="partner_id">
              <TextField value={partnerId} onChange={(e) => setPartnerId(e.target.value)} placeholder="例: 83825410" />
            </FilterCell>
            <Button onClick={load} small>
              <MdRefresh style={{ verticalAlign: 'middle', marginRight: 4 }} />
              再取得
            </Button>
            {loading && <InlineSpinner isLoading={true} />}
          </div>
        </div>
      </CardBase>

      {/* Events table */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>
            <MdHistory style={{ verticalAlign: 'middle', marginRight: 4 }} />
            イベント trail ({events.length} 件)
          </SubSectionTitle>
          <div style={{ marginTop: '8px', overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                  <th style={th}>at</th>
                  <th style={th}>event_type</th>
                  <th style={th}>source_path</th>
                  <th style={th}>outcome</th>
                  <th style={{ ...th, textAlign: 'right' }}>wallet_txn / partner</th>
                  <th style={{ ...th, textAlign: 'right' }}>pattern updated?</th>
                  <th style={th}>payload</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => (
                  <tr key={ev.id} style={{ borderBottom: '1px solid #f0eeec' }}>
                    <td style={{ ...td, fontSize: '0.6875rem' }}>{fmtDate(ev.created_at)}</td>
                    <td style={{ ...td, fontWeight: 600, color: colorForEventType(ev.event_type) }}>
                      {ev.event_type}
                    </td>
                    <td style={{ ...td, fontSize: '0.6875rem' }}>{ev.source_path || '—'}</td>
                    <td style={{ ...td, fontSize: '0.6875rem' }}>{ev.outcome || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontSize: '0.6875rem' }}>
                      {ev.wallet_txn_id ? `wt:${ev.wallet_txn_id}` : (ev.partner_id ? `partner:${ev.partner_id}` : '—')}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {ev.pattern_index_updated ? <MdCheckCircle style={{ color: '#338650' }} /> : '—'}
                    </td>
                    <td style={{ ...td, fontSize: '0.6875rem', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.payload_json || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {events.length === 0 && !loading && <Note mt={0.5}>該当 event なし</Note>}
          </div>
        </div>
      </CardBase>

      {/* Breakdown */}
      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>
            <MdInsights style={{ verticalAlign: 'middle', marginRight: 4 }} />
            event_type × source_path breakdown
          </SubSectionTitle>
          <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                <th style={th}>event_type</th>
                <th style={th}>source_path</th>
                <th style={{ ...th, textAlign: 'right' }}>count</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((b, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0eeec' }}>
                  <td style={{ ...td, fontWeight: 600, color: colorForEventType(b.event_type) }}>{b.event_type}</td>
                  <td style={{ ...td, fontSize: '0.6875rem' }}>{b.source_path || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{b.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBase>
    </>
  );
};

// ---------------------------------------------------------------------------
// ConflictsView
// ---------------------------------------------------------------------------
const ConflictsView: React.FC = () => {
  const [conflicts, setConflicts] = useState<LearningConflict[]>([]);
  const [stats, setStats] = useState<any>({});
  const [resolved, setResolved] = useState<'true' | 'false' | ''>('false');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchLearningConflicts({
        resolved: resolved || undefined,
        limit: 200,
      });
      setConflicts(r.conflicts);
      setStats(r.stats);
    } catch (e: any) {
      setError(e?.message || String(e));
    }
    setLoading(false);
  }, [resolved]);

  useEffect(() => { load(); }, [load]);

  const onResolve = async (conflictId: number, choice: 'a_wins' | 'b_wins' | 'manual_override') => {
    setResolvingId(conflictId);
    try {
      await resolveLearningConflict({ conflict_id: conflictId, resolution: choice });
      await load();
    } catch (e: any) {
      setError(e?.message || String(e));
    }
    setResolvingId(null);
  };

  return (
    <>
      {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px', display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
          <Kpi icon={<MdCompareArrows />} label="conflict 総数" value={String(stats.total || 0)} color="#7c5cff" />
          <Kpi icon={<MdWarning />} label="未解決" value={String(stats.unresolved || 0)}
               color={(stats.unresolved || 0) > 0 ? '#dd7a00' : '#8a8580'} />
          <Kpi icon={<MdCheckCircle />} label="解決済" value={String(stats.resolved || 0)} color="#338650" />
          <Kpi icon={<MdAccountTree />} label="conflict 業者数" value={String(stats.partners_with_conflicts || 0)} color="#2e7cf6" />
          <FilterCell label="resolved filter">
            <SelectBox
              options={[
                { name: 'すべて', value: '' },
                { name: '未解決のみ', value: 'false' },
                { name: '解決済のみ', value: 'true' },
              ]}
              value={resolved}
              onChange={(e) => setResolved(e.target.value as any)}
            />
          </FilterCell>
          <Button onClick={load} small>
            <MdRefresh style={{ verticalAlign: 'middle', marginRight: 4 }} />
            再取得
          </Button>
          {loading && <InlineSpinner isLoading={true} />}
        </div>
      </CardBase>

      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>
            学習 conflict 一覧 ({conflicts.length} 件)
          </SubSectionTitle>
          <Note mt={0.25}>同一 partner で 異なる学習 hook が 異なる結論を出したケース。 user が判断して resolve。</Note>
          <div style={{ marginTop: '8px', overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                  <th style={th}>detected_at</th>
                  <th style={th}>partner</th>
                  <th style={th}>field</th>
                  <th style={th}>A source</th>
                  <th style={th}>A value</th>
                  <th style={th}>B source</th>
                  <th style={th}>B value</th>
                  <th style={th}>resolution</th>
                  <th style={th}>actions</th>
                </tr>
              </thead>
              <tbody>
                {conflicts.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f0eeec' }}>
                    <td style={{ ...td, fontSize: '0.6875rem' }}>{fmtDate(c.detected_at)}</td>
                    <td style={td}>{c.partner_name || `partner_${c.partner_id}`}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{c.conflict_field}</td>
                    <td style={{ ...td, fontSize: '0.6875rem' }}>{c.hook_a_source}</td>
                    <td style={td}>{c.hook_a_value || '—'}</td>
                    <td style={{ ...td, fontSize: '0.6875rem' }}>{c.hook_b_source}</td>
                    <td style={td}>{c.hook_b_value || '—'}</td>
                    <td style={{ ...td, fontWeight: 600,
                      color: c.resolution ? '#338650' : '#dd7a00' }}>
                      {c.resolution || '未解決'}
                    </td>
                    <td style={td}>
                      {!c.resolution && (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <Button onClick={() => onResolve(c.id, 'a_wins')} small primary disabled={resolvingId === c.id}>A</Button>
                          <Button onClick={() => onResolve(c.id, 'b_wins')} small primary disabled={resolvingId === c.id}>B</Button>
                          <Button onClick={() => onResolve(c.id, 'manual_override')} small disabled={resolvingId === c.id}>手動</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {conflicts.length === 0 && !loading && <Note mt={0.5}>conflict なし</Note>}
          </div>
        </div>
      </CardBase>
    </>
  );
};

// ---------------------------------------------------------------------------
// BreakdownView (= 統計 dashboard)
// ---------------------------------------------------------------------------
const BreakdownView: React.FC = () => {
  const [breakdown, setBreakdown] = useState<LearningEventBreakdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetchLearningEvents({ limit: 1 });
      setBreakdown(r.breakdown);
    } catch (e: any) {
      setError(e?.message || String(e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // event_type 単位の集計 (= source_path 集約)
  const byEventType = useMemo(() => {
    const m: Record<string, number> = {};
    for (const b of breakdown) {
      m[b.event_type] = (m[b.event_type] || 0) + b.n;
    }
    return Object.entries(m).map(([k, v]) => ({ event_type: k, n: v })).sort((a, b) => b.n - a.n);
  }, [breakdown]);

  // source_path 単位の集計
  const bySource = useMemo(() => {
    const m: Record<string, number> = {};
    for (const b of breakdown) {
      const k = b.source_path || '(null)';
      m[k] = (m[k] || 0) + b.n;
    }
    return Object.entries(m).map(([k, v]) => ({ source_path: k, n: v })).sort((a, b) => b.n - a.n);
  }, [breakdown]);

  // Phase 4-B specific event types
  const phase4bEvents = useMemo(() => {
    const phase4bTypes = new Set([
      'vision_paradigm_learn', 'past_deal_autofill', '3way_to_bank_link',
      'category_canonical', 'auto_debit_detected', 'multi_store_split_learn',
    ]);
    return byEventType.filter(b => phase4bTypes.has(b.event_type));
  }, [byEventType]);

  return (
    <>
      {error && <MessageBlock error mb={0.5}>{error}</MessageBlock>}

      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>
            <MdSchool style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Phase 4-B 新 hook 発火状況
          </SubSectionTitle>
          <Note mt={0.25}>2026-05-11 追加 6 hook の発火回数。 worker deploy 後の累積。</Note>
          <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                <th style={th}>event_type (Phase 4-B)</th>
                <th style={{ ...th, textAlign: 'right' }}>発火回数</th>
              </tr>
            </thead>
            <tbody>
              {phase4bEvents.length === 0 ? (
                <tr><td colSpan={2} style={{ ...td, color: '#8a8580' }}>まだ発火していません (= deploy 直後または該当 path 未走行)</td></tr>
              ) : (
                phase4bEvents.map(b => (
                  <tr key={b.event_type} style={{ borderBottom: '1px solid #f0eeec' }}>
                    <td style={{ ...td, fontWeight: 600, color: colorForEventType(b.event_type) }}>{b.event_type}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{b.n}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardBase>

      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>
            event_type 別 発火回数 (全期間)
          </SubSectionTitle>
          <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                <th style={th}>event_type</th>
                <th style={{ ...th, textAlign: 'right' }}>count</th>
              </tr>
            </thead>
            <tbody>
              {byEventType.map(b => (
                <tr key={b.event_type} style={{ borderBottom: '1px solid #f0eeec' }}>
                  <td style={{ ...td, fontWeight: 600, color: colorForEventType(b.event_type) }}>{b.event_type}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{b.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBase>

      <CardBase paddingSize="small" mb={0.5}>
        <div style={{ padding: '12px' }}>
          <SubSectionTitle headlineLevel={4}>
            source_path 別 発火回数 (全期間)
          </SubSectionTitle>
          <table style={{ width: '100%', marginTop: '8px', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#f7f5f5', textAlign: 'left' }}>
                <th style={th}>source_path</th>
                <th style={{ ...th, textAlign: 'right' }}>count</th>
              </tr>
            </thead>
            <tbody>
              {bySource.map(b => (
                <tr key={b.source_path} style={{ borderBottom: '1px solid #f0eeec' }}>
                  <td style={{ ...td, fontSize: '0.78rem' }}>{b.source_path}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{b.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBase>
    </>
  );
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
function colorForEventType(eventType: string): string {
  if (eventType === 'reconcile_success' || eventType === 'approve' || eventType === 'mark_registered' || eventType === 'past_deal_autofill') return '#338650';
  if (eventType === 'reject') return '#c33939';
  if (eventType === 'freee_auto_sync') return '#2e7cf6';
  if (eventType.includes('paradigm') || eventType.includes('canonical')) return '#7c5cff';
  if (eventType === 'auto_debit_detected' || eventType.includes('split_learn') || eventType.includes('3way_to_bank')) return '#dd7a00';
  if (eventType === 'manual_chat_learning') return '#2e7cf6';
  return '#6b6560';
}

const th: React.CSSProperties = { padding: '6px 8px', fontSize: '0.6875rem', color: '#6b6560', fontWeight: 500 };
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };

const FilterCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '180px' }}>
    <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>{label}</div>
    {children}
  </div>
);

const Kpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
}> = ({ icon, label, value, sub, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minWidth: '120px' }}>
    <div style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ color, fontSize: '1rem', display: 'inline-flex' }}>{icon}</span>
      {label}
    </div>
    <div style={{ fontSize: '1.125rem', fontWeight: 600, color }}>{value}</div>
    {sub && <div style={{ fontSize: '0.6875rem', color: '#8a8580' }}>{sub}</div>}
  </div>
);
